import React, { CSSProperties, useEffect, useRef } from "react";

import "./LoginButton.scss";

type GoogleLoginButtonProps = {
  style?: CSSProperties;
  boxStyle?: CSSProperties;
  clientId: string;
  disabled?: boolean;
  googleLoginText?: string;
  logoSrc?: string;
  logoStyle?: CSSProperties;
  shimmer?: boolean;
  spanStyle?: CSSProperties;
  textStyle?: CSSProperties;
  onCallback?: (data: any) => void;
  onClick?: () => void;
};

const SCRIPT_ID = "google-identity-services";
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

const GoogleLoginButton = React.forwardRef<HTMLDivElement, GoogleLoginButtonProps>((props, ref) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadScript = () => {
      if (document.querySelector(`#${SCRIPT_ID}`)) {
        return;
      }

      const script = document.createElement("script");

      script.src = SCRIPT_SRC;
      script.id = SCRIPT_ID;
      document.head.append(script);
    };

    const initializeGoogleAuth = () => {
      if (!globalThis.google?.accounts?.oauth2 || !buttonRef.current) {
        return;
      }

      const client = globalThis.google.accounts.oauth2.initCodeClient({
        client_id: props.clientId,
        scope: "openid email profile https://www.googleapis.com/auth/calendar.app.created",
        callback: props.onCallback,
        ux_mode: "popup",
        access_type: "offline",
      });

      (buttonRef.current as any).__googleCodeClient = client;
    };

    loadScript();

    if (globalThis.google?.accounts?.oauth2) {
      initializeGoogleAuth();

      return;
    }

    const script = document.querySelector(`#${SCRIPT_ID}`);

    script?.addEventListener("load", initializeGoogleAuth);

    return () => {
      script?.removeEventListener("load", initializeGoogleAuth);
    };
  }, [props.clientId, props.onCallback]);

  const handleClick = () => {
    if (!props.disabled && !props.onClick) {
      if (buttonRef.current) {
        const client = (buttonRef.current as any).__googleCodeClient;

        client?.requestCode();
      }

      return;
    }

    if (!props.disabled) {
      props.onClick?.();
    }
  };

  return (
    <div
      className={`google-login-container ${props.shimmer && !props.disabled ? "shimmer" : ""}`}
      style={{ height: "49.6px", position: "relative", ...props.boxStyle }}
      onClick={handleClick}
    >
      <div ref={ref} className="googleLoginButton" style={{ ...props.style }}>
        <img style={props.logoStyle} alt="" src={props.logoSrc || ""} />
        <div style={{ display: "flex", ...props.textStyle }}>
          <span style={props.spanStyle}>{props.googleLoginText ?? "เข้าสู่ระบบด้วย Google"}</span>
        </div>
      </div>
      <div ref={buttonRef} style={{ display: "none" }} />
    </div>
  );
});

GoogleLoginButton.displayName = "GoogleLoginButton";

export default GoogleLoginButton;